import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOrderDetail, getOrderReviewable } from '../../services/orderService';
import ShipmentTracker from './ShipmentTracker';
import ImageGallery from './ImageGallery';
import OrderStatusBadge from './atoms/OrderStatusBadge';
import { IoChevronDownOutline } from "react-icons/io5";
import { miladiToShamsi } from "../../utils/miladiToShamsi";
import { Link } from "react-router-dom";
import RenderReviewButton from "./Rating/RenderReviewButton";

const UNIT_TRANSLATIONS = {
  count: 'عدد',
  package: 'بسته',
  weight: 'کیلو گرم',
  length: 'متر',
  area: 'متر مربع',
  litr: 'لیتر',
  volume: 'متر مکعب',
};

const OrderAccordion = ({ order }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: orderDetails, isLoading } = useQuery({
    queryKey: ['orderDetail', order.id],
    queryFn: () => getOrderDetail(order.id),
    enabled: isOpen,
  });

  const { data: reviewables } = useQuery({
    queryKey: ['orderReviewables', order.id],
    queryFn: () => getOrderReviewable(order.id),
    enabled: isOpen,
  });

  const shipmentReviewMap = {};
  const itemReviewMap = {};

  if (reviewables?.shipments) {
    reviewables.shipments.forEach((shipment) => {
      shipmentReviewMap[shipment.id] = shipment.review;

      shipment.items.forEach((item) => {
        itemReviewMap[item.id] = item.review;
      });
    });
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              سفارش
            </span>
            <span className="text-sm font-semibold text-gray-900 sm:text-base">
              ORD-{order.id}
            </span>
          </div>

          <p className="mt-1 text-[11px] leading-5 text-gray-500 sm:text-xs">
            {miladiToShamsi(order.created_at)}
          </p>
        </div>

        <div className="shrink-0">
          <OrderStatusBadge status={order.fulfillment_status} />
        </div>
      </div>

      <div className="px-4 py-4 sm:px-5">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3">
          <div className="rounded-lg bg-gray-50 px-3 py-3">
            <span className="block text-[10px] font-medium text-gray-400 sm:text-[11px]">
              تاریخ ثبت
            </span>
            <span className="mt-1 block text-sm font-medium tabular-nums text-gray-700 sm:text-[14px]">
              {miladiToShamsi(order.created_at)}
            </span>
          </div>

          <div className="rounded-lg bg-gray-50 px-3 py-3">
            <span className="block text-[10px] font-medium text-gray-400 sm:text-[11px]">
              مبلغ کل
            </span>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
              <span className="text-sm font-semibold tabular-nums text-gray-900 sm:text-[14px]">
                {Number(order.total_amount).toLocaleString()}
              </span>
              <span className="text-[10px] text-gray-400">تومان</span>
            </div>
          </div>

          <div className="rounded-lg bg-gray-50 px-3 py-3">
            <span className="block text-[10px] font-medium text-gray-400 sm:text-[11px]">
              تعداد اقلام
            </span>
            <span className="mt-1 block text-sm font-medium text-gray-700 sm:text-[14px]">
              {order.items_count} قلم کالا
            </span>
          </div>
        </div>

        <div className="pt-4">
          <p className="mb-2 text-[10px] font-medium text-gray-400 sm:text-[11px]">
            کالاهای این سفارش
          </p>
          <ImageGallery items={order.items} />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-600 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-100 sm:px-5"
      >
        <span>{isOpen ? 'بستن جزئیات' : 'مشاهده جزئیات'}</span>
        <IoChevronDownOutline
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 bg-gray-50 px-3 py-4 sm:px-5">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-[#1E3A5F]" />
            </div>
          ) : (
            <div className="space-y-5">
              {orderDetails?.shipments?.map((shipment) => {
                const shipmentReview = shipmentReviewMap[shipment.id];

                return (
                  <section key={shipment.id} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 sm:text-base">
                        <span className="h-4 w-1.5 rounded-full bg-[#1E3A5F]" />
                        محموله SHI-{shipment.id}
                      </h3>

                      <RenderReviewButton
                        review={shipmentReview}
                        status={shipment.status}
                        entity={{
                          entityType: "shipment",
                          shipmentId: shipment.id,
                          orderId: orderDetails?.id,
                          info: shipment,
                        }}
                      />
                    </div>

                    <ShipmentTracker shipment={shipment} isCustomer={true} />

                    <div className="mt-3 overflow-x-auto rounded-lg border border-gray-100">
                      <table className="w-full min-w-[720px] text-right text-sm">
                        <thead className="bg-gray-50 text-[11px] font-medium text-gray-600 sm:text-xs">
                          <tr>
                            <th className="px-3 py-3">کالا</th>
                            <th className="px-2 py-3 text-center">مقدار</th>
                            <th className="px-2 py-3 text-center">قیمت واحد</th>
                            <th className="px-2 py-3 text-center">تخفیف</th>
                            <th className="px-2 py-3 text-center font-semibold text-[#1E3A5F]">
                              قیمت نهایی
                            </th>
                            <th className="px-2 py-3 text-center">نظر</th>
                          </tr>
                        </thead>

                        <tbody className="text-[13px] text-gray-700 sm:text-sm">
                          {shipment.items.map((item) => {
                            const itemReview = itemReviewMap[item.id];

                            return (
                              <tr
                                key={item.id}
                                className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                              >
                                <td className="px-3 py-3 align-top">
                                  <div className="flex items-center gap-3">
                                    <Link
                                      to={`/shopping/product-detail/${item.product_slug}`}
                                      className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 sm:h-14 sm:w-14"
                                    >
                                      <img
                                        src={item.product_image}
                                        alt=""
                                        className="h-full w-full object-cover"
                                      />
                                    </Link>

                                    <div className="min-w-0">
                                      <p className="line-clamp-2 text-sm font-semibold text-gray-900 sm:text-[14px]">
                                        {item.product_name}
                                      </p>
                                      <p className="mt-1 text-[10px] text-gray-400 sm:text-[11px]">
                                        {item.product_category} | {item.product_brand}
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-2 py-3 text-center align-top font-medium text-gray-700">
                                  <span className="block tabular-nums">{item.qty_ordered}</span>
                                  <span className="mt-1 block text-[10px] text-gray-400">
                                    {UNIT_TRANSLATIONS[item.unit_of_measure]}
                                  </span>
                                </td>

                                <td className="px-2 py-3 text-center align-top tabular-nums text-gray-700">
                                  {Number(item.unit_price).toLocaleString()}
                                  <span className="mt-1 block text-[10px] text-gray-400">
                                    تومان
                                  </span>
                                </td>

                                <td className="px-2 py-3 text-center align-top tabular-nums text-[#E74C3C]">
                                  {item.discount_percent}%
                                  <span className="mt-1 block text-[10px] text-gray-400">
                                    ({Number(item.discount_amount).toLocaleString()} تومان)
                                  </span>
                                </td>

                                <td className="px-2 py-3 text-center align-top font-semibold tabular-nums text-gray-900">
                                  {Number(item.final_total).toLocaleString()}
                                  <span className="mt-1 block text-[10px] font-normal text-gray-400">
                                    تومان
                                  </span>
                                </td>

                                <td className="px-2 py-3 text-center align-top">
                                  <RenderReviewButton
                                    review={itemReview}
                                    status={item.status}
                                    entity={{
                                      entityType: "shipment_item",
                                      shipmentId: shipment.id,
                                      orderId: orderDetails?.id,
                                      shipmentItemId: item.id,
                                      info: item,
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderAccordion;
